import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchLoginLocation, logLogin, setCurrentActorName, resolveActorNameFromEmail } from '../services/activityLog';
import { verifyAdminCredentials } from '../services/adminAuth';
import { fetchUserProfile } from '../services/userProfile';
import { isAdminEmail } from '../utils/adminRoles';

const AuthContext = createContext(null);

const SESSION_KEY = 'psag-admin-session';
const EMAIL_KEY = 'psag-admin-email';

const EMPTY_PROFILE = { displayName: '', avatarUrl: '' };

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userProfile, setUserProfile] = useState(EMPTY_PROFILE);

  const refreshUserProfile = useCallback(async (email = userEmail) => {
    if (!email) {
      setUserProfile(EMPTY_PROFILE);
      return EMPTY_PROFILE;
    }

    const profile = await fetchUserProfile(email);
    setUserProfile(profile);
    setCurrentActorName(profile.displayName || resolveActorNameFromEmail(email));
    return profile;
  }, [userEmail]);

  const syncSession = useCallback(() => {
    const session = sessionStorage.getItem(SESSION_KEY);
    const active = session === 'true';
    const email = sessionStorage.getItem(EMAIL_KEY) || '';
    setIsAuthenticated(active);
    setUserEmail(email);

    if (active && email) {
      refreshUserProfile(email);
    } else {
      setUserProfile(EMPTY_PROFILE);
    }
  }, [refreshUserProfile]);

  useEffect(() => {
    syncSession();
    setIsLoading(false);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncSession();
    };

    window.addEventListener('focus', syncSession);
    window.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', syncSession);
      window.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [syncSession]);

  const signIn = useCallback(async (email, password) => {
    const result = await verifyAdminCredentials(email, password);

    if (!result.ok) {
      return { success: false, error: result.error || 'Invalid email or password.' };
    }

    const normalizedEmail = email.trim().toLowerCase();
    sessionStorage.setItem(SESSION_KEY, 'true');
    sessionStorage.setItem(EMAIL_KEY, normalizedEmail);
    setIsAuthenticated(true);
    setUserEmail(normalizedEmail);
    setShowSignIn(false);
    const profile = await refreshUserProfile(normalizedEmail);
    setCurrentActorName(profile.displayName || resolveActorNameFromEmail(normalizedEmail));

    const location = await fetchLoginLocation();
    logLogin({
      email: normalizedEmail,
      displayName: profile.displayName,
      page: window.location.pathname,
      location,
    });

    return { success: true };
  }, [refreshUserProfile]);

  const signOut = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
    setCurrentActorName('');
    setIsAuthenticated(false);
    setUserEmail('');
    setUserProfile(EMPTY_PROFILE);
    setShowProfileSettings(false);
  }, []);

  const openSignIn = useCallback(() => setShowSignIn(true), []);
  const closeSignIn = useCallback(() => setShowSignIn(false), []);
  const openProfileSettings = useCallback(() => setShowProfileSettings(true), []);
  const closeProfileSettings = useCallback(() => setShowProfileSettings(false), []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        isEditing: isAuthenticated,
        isAdmin: isAdminEmail(userEmail),
        userEmail,
        userProfile,
        refreshUserProfile,
        showSignIn,
        showProfileSettings,
        signIn,
        signOut,
        openSignIn,
        closeSignIn,
        openProfileSettings,
        closeProfileSettings,
        syncSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
